(() => {
  const namespace = 'ProlabHeroMotion';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const selector = '[data-prolab-hero-motion]';
  const heroes = new Set();
  const activeHeroes = new Set();
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const intensityMultipliers = {
    subtle: 0.6,
    dynamic: 1,
    aggressive: 1.35,
  };
  let animationFrame = 0;

  const resetScrollMotion = (hero) => {
    hero.style.setProperty('--prolab-hero-scroll-x', '0px');
    hero.style.setProperty('--prolab-hero-scroll-y', '0px');
    hero.style.setProperty('--prolab-hero-scroll-zoom', '0');
  };

  const updateHero = (hero) => {
    if (!hero.isConnected) {
      activeHeroes.delete(hero);
      heroes.delete(hero);
      return;
    }

    const isMobile = mobileQuery.matches;
    const scrollEnabled = hero.dataset.scrollMotion === 'true';
    const mobileEnabled = hero.dataset.mobileMotion === 'true';

    if (reducedMotionQuery.matches || !scrollEnabled || (isMobile && !mobileEnabled)) {
      resetScrollMotion(hero);
      return;
    }

    const rect = hero.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, -rect.top / Math.max(rect.height, 1)));
    const easedProgress = progress * progress * (3 - 2 * progress);
    const intensity = intensityMultipliers[hero.dataset.scrollMotionIntensity] || 1;
    const desktopMovement = Number(hero.dataset.desktopScrollMovement) || 0;
    const desktopVertical = Number(hero.dataset.desktopScrollVertical) || 0;
    const desktopZoom = Number(hero.dataset.desktopScrollZoom) || 0;
    const mobileMovement = Math.min(Number(hero.dataset.mobileScrollMovement) || 0, desktopMovement * 0.4);
    const mobileZoom = Math.min(Number(hero.dataset.mobileScrollZoom) || 0, desktopZoom * 0.4);
    const movement = (isMobile ? mobileMovement : desktopMovement) * intensity;
    const verticalMovement = desktopVertical * (isMobile ? 0.35 : 1) * intensity;
    const zoom = (isMobile ? mobileZoom : desktopZoom) * intensity;

    hero.style.setProperty('--prolab-hero-scroll-x', `${(-movement * easedProgress).toFixed(2)}px`);
    hero.style.setProperty('--prolab-hero-scroll-y', `${(verticalMovement * easedProgress).toFixed(2)}px`);
    hero.style.setProperty('--prolab-hero-scroll-zoom', (zoom * easedProgress / 100).toFixed(4));
  };

  const render = () => {
    animationFrame = 0;
    activeHeroes.forEach(updateHero);
  };

  const scheduleRender = () => {
    if (!animationFrame) animationFrame = window.requestAnimationFrame(render);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        activeHeroes.add(entry.target);
        updateHero(entry.target);
      } else {
        activeHeroes.delete(entry.target);
      }
    });
  }, { rootMargin: '10% 0px' });

  const finishEntrance = (hero) => {
    hero.classList.add('is-entrance-complete');
  };

  const initHero = (hero) => {
    if (hero.dataset.motionInitialized === 'true') return;

    hero.dataset.motionInitialized = 'true';
    heroes.add(hero);
    observer.observe(hero);

    if (reducedMotionQuery.matches || !hero.classList.contains('prolab-hero--media-entrance')) {
      finishEntrance(hero);
    } else {
      const mediaMotion = hero.querySelector('.prolab-hero__media-motion');
      const duration = Number(hero.dataset.entranceDuration) || 1000;
      const onAnimationEnd = (event) => {
        if (event.animationName === 'prolab-hero-media-enter') finishEntrance(hero);
      };

      mediaMotion?.addEventListener('animationend', onAnimationEnd, { once: true });
      window.setTimeout(() => finishEntrance(hero), duration + 150);
    }

    updateHero(hero);
  };

  const init = (root = document) => {
    if (root.matches?.(selector)) initHero(root);
    root.querySelectorAll?.(selector).forEach(initHero);
  };

  const destroy = (root) => {
    const candidates = root.matches?.(selector) ? [root] : root.querySelectorAll?.(selector) || [];
    candidates.forEach((hero) => {
      observer.unobserve(hero);
      activeHeroes.delete(hero);
      heroes.delete(hero);
    });
  };

  const handleMotionPreference = () => {
    heroes.forEach((hero) => {
      if (reducedMotionQuery.matches) finishEntrance(hero);
      updateHero(hero);
    });
    scheduleRender();
  };

  window.addEventListener('scroll', scheduleRender, { passive: true });
  window.addEventListener('resize', scheduleRender, { passive: true });
  mobileQuery.addEventListener('change', handleMotionPreference);
  reducedMotionQuery.addEventListener('change', handleMotionPreference);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window[namespace] = { init, destroy };
  init(document);
})();
